<template>
  <div style="padding: 20px;">
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2">Carregando campanha...</p>
    </div>

    <div v-if="!loading && loadError">
      <div class="text-center py-5">
        <h4>Erro ao carregar campanha</h4>
        <p class="text-muted">{{ loadError }}</p>
        <b-button variant="primary" size="sm" @click="loadCampaign" class="mr-50">Tentar Novamente</b-button>
        <b-button variant="outline-secondary" size="sm" @click="$router.push({ name: 'crm-campanhas' })">Voltar</b-button>
      </div>
    </div>

    <div v-if="!loading && campaign">
      <div class="page-header" style="margin-bottom: 1rem;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 0.75rem;">
          <div>
            <div style="margin-bottom: 0.375rem;">
              <b-button variant="flat-secondary" size="sm" @click="$router.push({ name: 'crm-campanhas' })">
                <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />
                Voltar
              </b-button>
            </div>
            <h2 style="font-size: 1.35rem; font-weight: 600; margin: 0;">{{ campaign.nome }}</h2>
            <p v-if="campaign.descricao" class="text-muted" style="margin: 0.25rem 0 0;">{{ campaign.descricao }}</p>
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
            <b-badge :variant="statusVariant(campaign.status)" pill>{{ statusLabel(campaign.status) }}</b-badge>
            <b-button
              v-if="campaign.status === 'rascunho' || campaign.status === 'pausada'"
              variant="success" size="sm"
              @click="activateCampaign"
              :disabled="activating"
            >
              <feather-icon icon="PlayIcon" size="14" class="mr-25" />
              Ativar
            </b-button>
            <b-button
              v-if="campaign.status === 'ativa'"
              variant="warning" size="sm"
              @click="pauseCampaign"
            >
              <feather-icon icon="PauseIcon" size="14" class="mr-25" />
              Pausar
            </b-button>
            <b-button variant="outline-danger" size="sm" @click="deleteCampaign">
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
          </div>
        </div>
      </div>

      <b-tabs v-model="activeTab" content-class="mt-1">
        <b-tab title="Cadência" active>
          <div style="padding: 1rem 0;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
              <h5 style="margin: 0;">Etapas da Cadência</h5>
              <b-button variant="primary" size="sm" @click="showStepModal = true">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Nova Etapa
              </b-button>
            </div>
            <div v-if="steps.length === 0" class="text-center text-muted py-3">
              <p>Nenhuma etapa configurada. Adicione etapas para definir o fluxo de e-mails.</p>
            </div>
            <div v-else>
              <div v-for="(step, idx) in steps" :key="step.id" style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 1rem; margin-bottom: 0.75rem;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div>
                    <b-badge variant="light-primary" pill class="mr-50">{{ step.delay_days === 0 ? 'Imediato' : 'Dia +' + step.delay_days }}</b-badge>
                    <strong>{{ step.subject }}</strong>
                  </div>
                  <div>
                    <b-button variant="flat-primary" size="sm" @click="editStep(step)">
                      <feather-icon icon="EditIcon" size="14" />
                    </b-button>
                    <b-button variant="flat-danger" size="sm" @click="removeStep(step.id)">
                      <feather-icon icon="Trash2Icon" size="14" />
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab title="Destinatários">
          <div style="padding: 1rem 0;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
              <h5 style="margin: 0;">Lista de Distribuição ({{ recipientsTotal }})</h5>
              <b-button variant="primary" size="sm" @click="showAddRecipientModal = true">
                <feather-icon icon="UserPlusIcon" size="14" class="mr-25" />
                Adicionar Clientes
              </b-button>
            </div>
            <b-table
              :items="recipients"
              :fields="recipientFields"
              :busy="recipientsLoading"
              responsive hover show-empty
              empty-text="Nenhum destinatário adicionado"
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center py-3"><b-spinner small variant="primary" /></div>
              </template>
              <template #cell(actions)="{ item }">
                <b-button variant="flat-danger" size="sm" @click="removeRecipient(item.id)">
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </template>
            </b-table>
            <div v-if="recipientsTotal > recipientsPerPage" class="mt-1">
              <b-pagination v-model="recipientsPage" :total-rows="recipientsTotal" :per-page="recipientsPerPage" size="sm" class="mb-0" @change="loadRecipients" />
            </div>
          </div>
        </b-tab>

        <b-tab title="Estatísticas">
          <div style="padding: 1rem 0;">
            <div v-if="statsLoading" class="text-center py-3"><b-spinner small variant="primary" /></div>
            <div v-if="!statsLoading && stats">
              <b-row>
                <b-col cols="4" md="2" v-for="(val, label) in { 'Destinatários': stats.recipients_count, 'Envios': (stats.totals||{}).total_sends||0, 'Enviados': (stats.totals||{}).enviado||0, 'Abertos': (stats.totals||{}).aberto||0, 'Pendentes': (stats.totals||{}).pendente||0, 'Falhas': (stats.totals||{}).falhou||0 }" :key="label" class="text-center mb-1">
                  <h3>{{ val }}</h3>
                  <small class="text-muted">{{ label }}</small>
                </b-col>
              </b-row>
            </div>
            <div v-if="!statsLoading && !stats" class="text-center text-muted py-3">
              <p>Nenhuma estatística disponível.</p>
            </div>
          </div>
        </b-tab>

        <b-tab title="Configurações">
          <div style="padding: 1rem 0;">
            <b-form @submit.prevent="saveSettings">
              <b-row>
                <b-col md="6">
                  <b-form-group label="Nome da campanha" label-for="s-name">
                    <b-form-input id="s-name" v-model="settingsForm.nome" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Prefixo do assunto" label-for="s-prefix">
                    <b-form-input id="s-prefix" v-model="settingsForm.subject_prefix" placeholder="Ex: [Hanes] " />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="Descrição" label-for="s-desc">
                    <b-form-textarea id="s-desc" v-model="settingsForm.descricao" rows="2" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-button variant="primary" size="sm" type="submit" :disabled="saving">
                <feather-icon icon="SaveIcon" size="14" class="mr-25" />
                Salvar
              </b-button>
            </b-form>
          </div>
        </b-tab>
      </b-tabs>
    </div>

    <b-modal
      v-model="showStepModal"
      :title="editingStep ? 'Editar Etapa' : 'Nova Etapa'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok="saveStep"
      :ok-disabled="!stepForm.subject || !stepForm.html_body"
      centered size="lg"
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="8">
          <b-form-group label="Assunto do e-mail" label-for="step-subject">
            <b-form-input id="step-subject" v-model="stepForm.subject" placeholder="Assunto do e-mail desta etapa" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Enviar após (dias)" label-for="step-delay">
            <b-form-input id="step-delay" v-model.number="stepForm.delay_days" type="number" min="0" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Corpo do e-mail">
        <quill-editor
          ref="emailEditor"
          v-model="stepForm.html_body"
          :options="editorOptions"
          class="email-editor"
        />
      </b-form-group>
      <div class="editor-variables-bar">
        <span class="var-label">Variáveis disponíveis:</span>
        <b-button variant="outline-primary" size="sm" pill class="var-btn" @click="insertVariable('nome')">Nome</b-button>
        <b-button variant="outline-primary" size="sm" pill class="var-btn" @click="insertVariable('email')">E-mail</b-button>
        <b-button variant="outline-primary" size="sm" pill class="var-btn" @click="insertVariable('campanha')">Campanha</b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="showAddRecipientModal"
      title="Adicionar Clientes"
      ok-title="Adicionar Selecionados"
      cancel-title="Cancelar"
      @ok="addSelectedRecipients"
      :ok-disabled="selectedClients.length === 0"
      centered size="lg"
      no-close-on-backdrop
    >
      <b-form-group label="Buscar clientes">
        <b-form-input v-model="clientSearch" placeholder="Razão social, fantasia ou CNPJ..." debounce="400" @update="searchClients" />
      </b-form-group>
      <div v-if="searchingClients" class="text-center py-2"><b-spinner small variant="primary" /></div>
      <div v-if="!searchingClients && searchResults.length > 0" class="client-results">
        <div
          v-for="cl in searchResults"
          :key="cl.clienteid"
          style="display: flex; align-items: center; padding: 0.5rem; border-bottom: 1px solid #eee; cursor: pointer;"
          :style="{ background: isClientSelected(cl.clienteid) ? '#f0f7ff' : 'transparent' }"
          @click="toggleClientSelection(cl)"
        >
          <div style="margin-right: 0.75rem;">
            <feather-icon :icon="isClientSelected(cl.clienteid) ? 'CheckSquareIcon' : 'SquareIcon'" size="16" />
          </div>
          <div style="flex: 1;">
            <strong>{{ cl.clientenomefantasia || cl.clienterazaosocial }}</strong>
            <span class="text-muted ml-50">{{ cl.clientecnpj }}</span>
          </div>
          <div :class="{ 'text-danger': !cl.clienteemailcomercial }">
            {{ cl.clienteemailcomercial || 'Sem e-mail' }}
          </div>
        </div>
      </div>
      <div v-if="!searchingClients && searchResults.length === 0 && clientSearch.length >= 2" class="text-center text-muted py-2">
        Nenhum cliente encontrado
      </div>
      <div v-if="selectedClients.length > 0" class="mt-1">
        <strong>{{ selectedClients.length }}</strong> cliente(s) selecionado(s)
      </div>
    </b-modal>
  </div>
</template>

<script>
import campaignClient from '@/api/CRM/campaign.client';
import {
  BButton, BBadge, BSpinner, BRow, BCol, BFormGroup,
  BModal, BFormInput, BTabs, BTab,
  BTable, BPagination, BForm,
} from 'bootstrap-vue';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CampanhaDetail',
  components: {
    BButton, BBadge, BSpinner, BRow, BCol, BFormGroup,
    BModal, BFormInput, BTabs, BTab,
    BTable, BPagination, BForm,
    quillEditor,
  },
  data() {
    return {
      loading: true,
      loadError: null,
      campaign: null,
      activeTab: 0,
      steps: [],
      recipients: [],
      recipientsTotal: 0,
      recipientsPage: 1,
      recipientsPerPage: 50,
      recipientsLoading: false,
      stats: null,
      statsLoading: false,
      activating: false,
      saving: false,
      showStepModal: false,
      editingStep: null,
      stepForm: { subject: '', html_body: '', delay_days: 0 },
      showAddRecipientModal: false,
      clientSearch: '',
      searchResults: [],
      searchingClients: false,
      selectedClients: [],
      settingsForm: { nome: '', descricao: '', subject_prefix: '' },
      editorOptions: {
        theme: 'snow',
        placeholder: 'Compose o corpo do e-mail aqui...',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote'],
            ['link', 'image'],
            [{ indent: '-1' }, { indent: '+1' }],
            ['clean'],
          ],
        },
      },
      recipientFields: [
        { key: 'nome', label: 'Nome' },
        { key: 'email', label: 'E-mail' },
        { key: 'emails_sent', label: 'Enviados', class: 'text-center' },
        { key: 'emails_opened', label: 'Abertos', class: 'text-center' },
        { key: 'emails_pending', label: 'Pendentes', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center', thStyle: 'width: 50px' },
      ],
    };
  },
  watch: {
    activeTab(val) {
      if (val === 1) this.loadRecipients();
      if (val === 2) this.loadStats();
    },
  },
  created() {
    console.log('[CampanhaDetail] created, route id:', this.$route.params.id);
    this.loadCampaign();
  },
  methods: {
    async loadCampaign() {
      this.loading = true;
      this.loadError = null;
      try {
        const id = this.$route.params.id;
        console.log('[CampanhaDetail] loadCampaign id:', id);
        if (!id) {
          this.loadError = 'ID da campanha não informado';
          return;
        }
        const campResult = await campaignClient.getById(id);
        console.log('[CampanhaDetail] API result:', JSON.stringify(campResult).substring(0, 200));
        const campaignData = campResult.data || campResult;
        if (!campaignData || !campaignData.id) {
          this.loadError = 'Campanha não encontrada (dados inválidos)';
          console.log('[CampanhaDetail] campaignData invalid:', campaignData);
          return;
        }
        this.campaign = campaignData;
        console.log('[CampanhaDetail] campaign set:', this.campaign.id, this.campaign.nome);
        this.settingsForm = {
          nome: this.campaign.nome,
          descricao: this.campaign.descricao || '',
          subject_prefix: this.campaign.subject_prefix || '',
        };
        try {
          const stepsResult = await campaignClient.listSteps(id);
          const stepsData = stepsResult.data || stepsResult;
          this.steps = Array.isArray(stepsData) ? stepsData : [];
        } catch (stepsErr) {
          console.error('Erro ao carregar etapas:', stepsErr);
          this.steps = [];
        }
      } catch (error) {
        const msg = error.response?.data?.message || error.message || 'Erro desconhecido';
        console.error('Erro ao carregar campanha:', msg, error);
        this.loadError = msg;
      } finally {
        this.loading = false;
      }
    },
    async loadRecipients() {
      this.recipientsLoading = true;
      try {
        const result = await campaignClient.listRecipients(this.campaign.id, { page: this.recipientsPage, limit: this.recipientsPerPage });
        const payload = result.data || result;
        this.recipients = payload.items || [];
        this.recipientsTotal = payload.total || 0;
      } catch (e) {
        console.error('Erro recipients:', e);
      } finally {
        this.recipientsLoading = false;
      }
    },
    async loadStats() {
      this.statsLoading = true;
      try {
        const result = await campaignClient.getStats(this.campaign.id);
        this.stats = result.data || result;
      } catch (e) {
        console.error('Erro stats:', e);
      } finally {
        this.statsLoading = false;
      }
    },
    async activateCampaign() {
      this.activating = true;
      try {
        const result = await campaignClient.activate(this.campaign.id);
        this.campaign = result.data || result;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            text: 'Campanha ativada',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: e.response?.data?.message || 'Erro ao ativar campanha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.activating = false;
      }
    },
    async pauseCampaign() {
      try {
        await campaignClient.pause(this.campaign.id);
        this.campaign.status = 'pausada';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Info',
            text: 'Campanha pausada',
            icon: 'InfoIcon',
            variant: 'warning',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao pausar campanha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async deleteCampaign() {
      if (!confirm('Deseja realmente excluir esta campanha?')) return;
      try {
        await campaignClient.remove(this.campaign.id);
        this.$router.push({ name: 'crm-campanhas' });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao excluir campanha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async saveSettings() {
      this.saving = true;
      try {
        const result = await campaignClient.update(this.campaign.id, this.settingsForm);
        this.campaign = result.data || result;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            text: 'Configurações salvas',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao salvar configurações',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.saving = false;
      }
    },
    editStep(step) {
      this.editingStep = step;
      this.stepForm = { subject: step.subject, html_body: step.html_body, delay_days: step.delay_days };
      this.showStepModal = true;
    },
    async saveStep(e) {
      e.preventDefault();
      try {
        if (this.editingStep) {
          await campaignClient.updateStep(this.campaign.id, this.editingStep.id, this.stepForm);
        } else {
          await campaignClient.createStep(this.campaign.id, this.stepForm);
        }
        this.showStepModal = false;
        this.editingStep = null;
        this.stepForm = { subject: '', html_body: '', delay_days: 0 };
        const stepsResult = await campaignClient.listSteps(this.campaign.id);
        this.steps = stepsResult.data || stepsResult;
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao salvar etapa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async removeStep(stepId) {
      if (!confirm('Excluir esta etapa?')) return;
      try {
        await campaignClient.deleteStep(this.campaign.id, stepId);
        this.steps = this.steps.filter(s => s.id !== stepId);
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao excluir etapa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async searchClients() {
      if (!this.clientSearch || this.clientSearch.length < 2) {
        this.searchResults = [];
        return;
      }
      this.searchingClients = true;
      try {
        const result = await campaignClient.searchClients(this.clientSearch);
        this.searchResults = result.data || result || [];
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao buscar clientes',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.searchingClients = false;
      }
    },
    isClientSelected(clienteid) {
      return this.selectedClients.some(c => c.clienteid === clienteid);
    },
    toggleClientSelection(client) {
      if (!client.clienteemailcomercial) return;
      const idx = this.selectedClients.findIndex(c => c.clienteid === client.clienteid);
      if (idx >= 0) this.selectedClients.splice(idx, 1);
      else this.selectedClients.push(client);
    },
    async addSelectedRecipients(e) {
      e.preventDefault();
      try {
        const clienteIds = this.selectedClients.map(c => c.clienteid);
        const result = await campaignClient.addRecipients(this.campaign.id, clienteIds);
        const data = result.data || result;
        let msg = `${data.added} cliente(s) adicionado(s)`;
        if (data.no_email > 0) msg += `, ${data.no_email} sem e-mail (ignorados)`;
        if (data.skipped > 0) msg += `, ${data.skipped} já existiam`;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            text: msg,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.showAddRecipientModal = false;
        this.selectedClients = [];
        this.clientSearch = '';
        this.searchResults = [];
        this.loadRecipients();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao adicionar destinatários',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async removeRecipient(recipientId) {
      try {
        await campaignClient.removeRecipient(this.campaign.id, recipientId);
        this.recipients = this.recipients.filter(r => r.id !== recipientId);
        this.recipientsTotal--;
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao remover destinatário',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    insertVariable(varName) {
      const quill = this.$refs.emailEditor ? this.$refs.emailEditor.quill : null;
      const text = '{{' + varName + '}}';
      if (quill) {
        quill.focus();
        const range = quill.getSelection(true) || { index: quill.getLength() - 1 };
        quill.insertText(range.index, text);
        quill.setSelection(range.index + text.length);
      } else {
        this.stepForm.html_body = (this.stepForm.html_body || '') + text;
      }
    },
    truncateHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      const text = div.textContent || div.innerText || '';
      return text.length > 120 ? text.substring(0, 120) + '...' : text;
    },
    statusVariant(s) {
      const map = { rascunho: 'secondary', ativa: 'success', pausada: 'warning', concluida: 'info' };
      return map[s] || 'light';
    },
    statusLabel(s) {
      const map = { rascunho: 'Rascunho', ativa: 'Ativa', pausada: 'Pausada', concluida: 'Concluída' };
      return map[s] || s;
    },
  },
};
</script>

<style scoped>
.campanha-detail-page { padding: 0.75rem; }
.page-header { margin-bottom: 1rem; }
.header-top { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 0.75rem; }
.header-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.back-row { margin-bottom: 0.375rem; }
.page-title { font-size: 1.35rem; font-weight: 600; color: var(--text-heading, #5e5873); margin-bottom: 0.15rem; }
.page-subtitle { color: var(--text-muted, #b9b9c3); font-size: 0.875rem; margin-bottom: 0; }

.tab-section {
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #ebe9f1);
  border-radius: 10px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(34,41,47,0.04);
}
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.section-header h5 { font-size: 0.95rem; font-weight: 600; color: var(--text-heading, #5e5873); margin: 0; }
.empty-mini { text-align: center; padding: 2rem 1rem; color: var(--text-muted, #b9b9c3); }
.empty-mini p { margin: 0; font-size: 0.875rem; }

.steps-timeline { display: flex; flex-direction: column; gap: 0; }
.step-card { display: flex; gap: 1rem; }
.step-indicator { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; width: 32px; }
.step-number {
  width: 28px; height: 28px; border-radius: 50%;
  background: #2563eb; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.75rem; font-weight: 700; flex-shrink: 0;
}
.step-line { width: 2px; flex: 1; background: var(--border-color, #ebe9f1); min-height: 20px; }
.step-content {
  flex: 1; padding: 0.75rem 1rem;
  background: var(--subtle-bg, #f8f8f8); border-radius: 8px;
  margin-bottom: 0.75rem; border: 1px solid var(--border-color, #ebe9f1);
}
.step-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.375rem; }
.step-delay-badge {
  display: inline-flex; align-items: center; gap: 0.2rem;
  font-size: 0.675rem; padding: 0.1rem 0.4rem; border-radius: 4px;
  background: rgba(37,99,235,0.1); color: #2563eb;
  margin-right: 0.375rem;
}
.step-subject { font-size: 0.85rem; color: var(--text-heading, #5e5873); }
.step-actions { display: flex; gap: 0.125rem; }
.step-preview { font-size: 0.775rem; color: var(--text-muted, #b9b9c3); line-height: 1.4; }

.recipients-table >>> thead th {
  background: var(--subtle-bg, #f8f8f8); font-size: 0.8rem; text-transform: uppercase;
  color: var(--text-heading, #5e5873); font-weight: 600; padding: 0.6rem 0.75rem;
}
.recipients-table >>> tbody td { padding: 0.5rem 0.75rem; font-size: 0.85rem; color: var(--text-body, #6e6b7b); }
.text-mono { font-family: monospace; font-size: 0.8rem; }
.count-badge {
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--subtle-bg, #f3f2f7); border-radius: 8px;
  padding: 0.1rem 0.5rem; font-size: 0.8rem; font-weight: 600;
  min-width: 24px;
}

.stats-overview { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.75rem; }
.stat-card {
  text-align: center; padding: 1rem;
  background: var(--subtle-bg, #f8f8f8); border-radius: 8px;
  border: 1px solid var(--border-color, #ebe9f1);
}
.stat-number { font-size: 1.5rem; font-weight: 700; color: var(--text-heading, #5e5873); }
.stat-label { font-size: 0.75rem; color: var(--text-muted, #b9b9c3); text-transform: uppercase; }
.stat-card-primary .stat-number { color: #2563eb; }
.stat-card-success .stat-number { color: #16a34a; }
.stat-card-info .stat-number { color: #0ea5e9; }
.stat-card-warning .stat-number { color: #f59e0b; }
.stat-card-danger .stat-number { color: #dc2626; }

.stats-steps h6 { font-size: 0.85rem; font-weight: 600; color: var(--text-heading, #5e5873); margin-bottom: 0.75rem; }
.step-stat-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.5rem 0.75rem; background: var(--subtle-bg, #f8f8f8);
  border-radius: 6px; margin-bottom: 0.375rem;
}
.ss-info { display: flex; align-items: center; gap: 0.5rem; font-size: 0.8rem; }
.ss-order { font-weight: 600; color: #2563eb; }
.ss-subject { color: var(--text-heading, #5e5873); }
.ss-delay { color: var(--text-muted, #b9b9c3); font-size: 0.725rem; }
.ss-bars { display: flex; gap: 0.25rem; }
.ss-pill {
  font-size: 0.65rem; padding: 0.1rem 0.35rem; border-radius: 4px; font-weight: 600;
}
.ss-sent { background: rgba(22,163,74,0.1); color: #16a34a; }
.ss-open { background: rgba(14,165,233,0.1); color: #0ea5e9; }
.ss-pend { background: rgba(245,158,11,0.1); color: #f59e0b; }
.ss-fail { background: rgba(220,38,38,0.1); color: #dc2626; }

.client-results { max-height: 300px; overflow-y: auto; border: 1px solid var(--border-color, #ebe9f1); border-radius: 6px; }
.client-result-item {
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.5rem 0.75rem; cursor: pointer;
  border-bottom: 1px solid var(--border-color, #ebe9f1);
  transition: background 0.15s;
}
.client-result-item:hover { background: var(--subtle-bg, #f8f8f8); }
.client-result-item.is-selected { background: rgba(37,99,235,0.06); }
.client-result-item:last-child { border-bottom: none; }
.cr-check { color: var(--text-muted, #b9b9c3); flex-shrink: 0; }
.is-selected .cr-check { color: #2563eb; }
.cr-info { flex: 1; min-width: 0; }
.cr-info strong { font-size: 0.85rem; color: var(--text-heading, #5e5873); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cr-cnpj { font-size: 0.725rem; color: var(--text-muted, #b9b9c3); }
.cr-email { font-size: 0.8rem; color: var(--text-body, #6e6b7b); flex-shrink: 0; }
.cr-email.no-email { color: #dc2626; font-style: italic; }
.selected-summary { font-size: 0.85rem; color: var(--text-heading, #5e5873); padding: 0.5rem; background: rgba(37,99,235,0.06); border-radius: 6px; text-align: center; }

.table-footer { display: flex; justify-content: flex-end; padding: 0.75rem 0; }

.editor-variables-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.625rem;
  padding: 0.5rem 0.75rem;
  background: var(--subtle-bg, #f8f8f8);
  border-radius: 6px;
  border: 1px solid var(--border-color, #ebe9f1);
}
.var-label {
  font-size: 0.775rem;
  color: var(--text-muted, #b9b9c3);
  font-weight: 500;
  white-space: nowrap;
}
.var-btn {
  font-size: 0.7rem !important;
  padding: 0.15rem 0.6rem !important;
}

@media (max-width: 768px) {
  .stats-overview { grid-template-columns: repeat(2, 1fr); }
  .step-stat-row { flex-direction: column; gap: 0.375rem; align-items: flex-start; }
  .ss-info { flex-wrap: wrap; }
}
</style>

<style>
body.dark-layout .tab-section { background: var(--dark-card-bg, #283046) !important; border-color: var(--dark-border, #3b4253) !important; }
body.dark-layout .step-content { background: rgba(255,255,255,0.04) !important; border-color: var(--dark-border, #3b4253) !important; }
body.dark-layout .step-subject { color: var(--dark-text-heading, #d0d2d6) !important; }
body.dark-layout .step-preview { color: var(--dark-text-muted, #676d7d) !important; }
body.dark-layout .step-line { background: var(--dark-border, #3b4253) !important; }
body.dark-layout .stat-card { background: rgba(255,255,255,0.04) !important; border-color: var(--dark-border, #3b4253) !important; }
body.dark-layout .stat-number { color: var(--dark-text-heading, #d0d2d6) !important; }
body.dark-layout .stat-card-primary .stat-number { color: #2563eb !important; }
body.dark-layout .stat-card-success .stat-number { color: #16a34a !important; }
body.dark-layout .stat-card-info .stat-number { color: #0ea5e9 !important; }
body.dark-layout .stat-card-warning .stat-number { color: #f59e0b !important; }
body.dark-layout .stat-card-danger .stat-number { color: #dc2626 !important; }
body.dark-layout .section-header h5, body.dark-layout .stats-steps h6 { color: var(--dark-text-heading, #d0d2d6) !important; }
body.dark-layout .step-stat-row { background: rgba(255,255,255,0.04) !important; }
body.dark-layout .ss-subject { color: var(--dark-text-heading, #d0d2d6) !important; }
body.dark-layout .client-result-item { border-color: var(--dark-border, #3b4253) !important; }
body.dark-layout .client-result-item:hover { background: rgba(255,255,255,0.04) !important; }
body.dark-layout .cr-info strong { color: var(--dark-text-heading, #d0d2d6) !important; }
body.dark-layout .client-results { border-color: var(--dark-border, #3b4253) !important; }
body.dark-layout .recipients-table >>> thead th { background: rgba(255,255,255,0.04) !important; color: var(--dark-text-heading, #d0d2d6) !important; }
body.dark-layout .recipients-table >>> tbody td { color: var(--dark-text-body, #b4b7bd) !important; }

.email-editor .ql-toolbar.ql-snow {
  border-radius: 8px 8px 0 0;
  border-color: var(--border-color, #ebe9f1);
  background: var(--subtle-bg, #f8f8f8);
}
.email-editor .ql-container.ql-snow {
  border-radius: 0 0 8px 8px;
  border-color: var(--border-color, #ebe9f1);
  min-height: 250px;
  font-size: 0.9rem;
}
.email-editor .ql-editor {
  min-height: 250px;
}
body.dark-layout .email-editor .ql-toolbar.ql-snow {
  background: rgba(255,255,255,0.04) !important;
  border-color: var(--dark-border, #3b4253) !important;
}
body.dark-layout .email-editor .ql-container.ql-snow {
  border-color: var(--dark-border, #3b4253) !important;
  background: var(--dark-card-bg, #283046) !important;
  color: var(--dark-text-body, #b4b7bd) !important;
}
body.dark-layout .email-editor .ql-editor.ql-blank::before {
  color: var(--dark-text-muted, #676d7d) !important;
}
body.dark-layout .email-editor .ql-snow .ql-stroke {
  stroke: var(--dark-text-body, #b4b7bd) !important;
}
body.dark-layout .email-editor .ql-snow .ql-fill {
  fill: var(--dark-text-body, #b4b7bd) !important;
}
body.dark-layout .email-editor .ql-snow .ql-picker-label {
  color: var(--dark-text-body, #b4b7bd) !important;
}
body.dark-layout .editor-variables-bar {
  background: rgba(255,255,255,0.04) !important;
  border-color: var(--dark-border, #3b4253) !important;
}
</style>
